'use client';

/**
 * ChatInput Component
 * 
 * Text input for sending messages to the chat assistant.
 */

import React, { useState, useRef, useEffect } from 'react';
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';
import { cn } from '@/utils/common/helpers';
import type { ChatDraft } from '@/hooks/chat/useChat';

interface ChatInputProps {
  onSend: (message: string, quickCommand?: string, files?: File[]) => Promise<boolean | void> | void;
  draftKey?: string | number;
  /** Put this text in the box (replacing what is there) without sending it. */
  draft?: ChatDraft | null;
  /** The draft was sent or edited, so it should not come back. */
  onDraftDone?: (id: number) => void;
  disabled?: boolean;
  placeholder?: string;
  showKeyboardHints?: boolean;
  /** Shown under the box in place of the keyboard hint. */
  footer?: React.ReactNode;
  onOpenQuickCommands?: () => void; // Callback to open quick commands menu
  /** Shown in place of the send button while an answer streams. */
  onStop?: () => void;
}

/** One box for Send and Stop so the slot never changes size when they swap. */
const ACTION_BUTTON_BOX =
  'flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200';

export function ChatInput({
  onSend,
  disabled = false,
  placeholder = 'Type a message...',
  showKeyboardHints = true,
  footer,
  onOpenQuickCommands,
  onStop,
  draftKey,
  draft,
  onDraftDone,
}: ChatInputProps) {
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [fileError, setFileError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const sendingRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const generationRef = useRef(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    generationRef.current += 1;
    setMessage(''); setFiles([]); setFileError('');
  }, [draftKey]);
  // After the reset above: a draft that is still held (not sent, not edited)
  // comes back after any reset, for example a conversation finishing loading
  // just after the Ask box opened the assistant.
  useEffect(() => {
    if (!draft) return;
    setMessage(draft.text);
    const box = textareaRef.current;
    if (box) {
      box.focus();
      box.setSelectionRange(draft.text.length, draft.text.length);
    }
    // A new draft or a reset applies it; the text rides with the id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.id, draftKey]);
  useEffect(() => {
    const urls = files.map(file => URL.createObjectURL(file));
    setPreviews(urls);
    return () => urls.forEach(url => URL.revokeObjectURL(url));
  }, [files]);
  const addFiles = (incoming: File[]) => {
    if (disabled || sendingRef.current) return;
    setFileError('');
    if (files.length + incoming.length > 3) { setFileError('You can attach up to 3 screenshots per message.'); return; }
    const invalid = incoming.find(file => !['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || !file.size || file.size > 10 * 1024 * 1024);
    if (invalid) { setFileError(`${invalid.name}: Choose a PNG, JPEG, or WebP smaller than 10 MB.`); return; }
    setFiles(previous => [...previous, ...incoming]);
  };
  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      // Reset to auto to get correct scrollHeight
      textareaRef.current.style.height = 'auto';
      
      // If message is empty, we want it to stay at natural 1-row height
      if (message.length > 0) {
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
      }
    }
  }, [message]);
  
  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if ((!message.trim() && !files.length) || disabled || sendingRef.current) return;
    sendingRef.current = true; setSubmitting(true); setFileError('');
    const generation = generationRef.current;
    try {
      const result = await onSend(message.trim() || 'Please help me with this screenshot.', undefined, files.length ? files : undefined);
      if (result !== false && generation === generationRef.current) {
        setMessage(''); setFiles([]);
        if (draft) onDraftDone?.(draft.id);
      }
    } catch (error) {
      if (generation === generationRef.current) setFileError(error instanceof Error ? error.message : 'Could not send. Please try again.');
    } finally { sendingRef.current = false; setSubmitting(false); }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };
  
  return (
    <form
      onSubmit={handleSubmit}
      className="border-t border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-900"
    >
      {files.length > 0 && <ul className="mb-2 flex gap-2 flex-wrap" aria-label="Selected screenshots">
        {files.map((file, index) => <li key={`${file.name}-${index}`} className="relative w-20">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={previews[index]} alt={file.name} className="h-16 w-20 rounded-lg object-contain bg-gray-100" />
          <button type="button" disabled={disabled || submitting} onClick={() => setFiles(previous => previous.filter((_, i) => i !== index))} aria-label={`Remove ${file.name}`} className="absolute right-0 top-0 rounded bg-white text-gray-900 px-1">×</button>
          <span className="block truncate text-xs text-gray-500">{file.name}</span>
        </li>)}
      </ul>}
      {fileError && <p role="alert" className="mb-2 text-xs text-red-600">{fileError}</p>}
      {submitting && <p role="status" className="mb-2 text-xs text-gray-500">Sending…</p>}
      <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" multiple aria-label="Choose screenshots" className="sr-only" disabled={disabled || submitting} onChange={e => { addFiles(Array.from(e.target.files || [])); e.target.value = ''; }} />
      <div className="flex items-center gap-2">
        <button type="button" aria-label="Attach screenshots" title="Attach screenshots" disabled={disabled || submitting} onClick={() => fileInputRef.current?.click()} className="flex-shrink-0 w-10 h-10 rounded-xl text-gray-600 hover:bg-gray-100 disabled:opacity-50"><GoogleMaterialIcon name="attach_file" size="sm" /></button>
        {/* Quick Commands Button */}
        {onOpenQuickCommands && (
          <button
            type="button"
            onClick={onOpenQuickCommands}
            disabled={disabled || submitting}
            className={cn(
              'flex-shrink-0 w-10 h-10 rounded-xl',
              'flex items-center justify-center',
              'transition-all duration-200',
              'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700',
              'text-gray-600 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            title="Quick Commands"
          >
            <GoogleMaterialIcon name="bolt" size="sm" />
          </button>
        )}
        
        <div className="flex-1 flex items-center">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              if (draft && e.target.value !== draft.text) onDraftDone?.(draft.id);
            }}
            onKeyDown={handleKeyDown}
            onPaste={e => { const images = Array.from(e.clipboardData.files); if (images.length) { e.preventDefault(); addFiles(images); } }}
            placeholder={placeholder}
            disabled={disabled || submitting}
            rows={1}
            className={cn(
              'w-full resize-none rounded-xl',
              'px-4 py-2.5',
              'bg-gray-50 dark:bg-gray-800',
              'border border-gray-200 dark:border-gray-700',
              'text-gray-900 dark:text-white text-sm',
              'placeholder-gray-400 dark:placeholder-gray-500',
              'focus:outline-none focus:border-brand-400 dark:focus:border-brand-500',
              'focus:shadow-[0_0_0_3px_rgba(99,102,241,0.15),0_0_20px_rgba(99,102,241,0.1)]',
              'dark:focus:shadow-[0_0_0_3px_rgba(129,140,248,0.2),0_0_20px_rgba(129,140,248,0.15)]',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'transition-all duration-300 ease-out'
            )}
            style={{ maxHeight: '120px' }}
          />
        </div>
        
        {disabled && onStop ? (
          <button
            type="button"
            onClick={onStop}
            aria-label="Stop"
            title="Stop"
            className={cn(
              ACTION_BUTTON_BOX,
              // Quiet gray, not solid black: it sits beside the input for the
              // whole answer and should not pull the eye (owner note, 2026-09-03).
              'bg-gray-200 hover:bg-gray-300 text-gray-700',
              'dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-100',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400'
            )}
          >
            <GoogleMaterialIcon name="stop" size="sm" iconStyle="filled" />
          </button>
        ) : (
          <button
            type="submit"
            aria-label="Send"
            disabled={disabled || submitting || (!message.trim() && !files.length)}
            className={cn(
              ACTION_BUTTON_BOX,
              (message.trim() || files.length) && !disabled && !submitting
                ? 'bg-brand-600 hover:bg-brand-700 text-white shadow-sm'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
            )}
          >
            {disabled ? (
              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <GoogleMaterialIcon name="send" size="sm" />
            )}
          </button>
        )}
      </div>
      
      {footer ?? (showKeyboardHints && (
        <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1.5 text-center">
          Press Enter to send, Shift+Enter for new line
        </p>
      ))}
    </form>
  );
}

export default ChatInput;
